import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fridaveAPI, roundsAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ScorecardView from '../../components/rounds/ScorecardView';

// ── Standings ─────────────────────────────────────────────────────────────────

function StandingsPanel({ season }) {
  const navigate = useNavigate();
  const [standings, setStandings] = useState([]);
  const [completedRounds, setCompletedRounds] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [sd, rd] = await Promise.all([
          fridaveAPI.getStandings(season).catch(() => []),
          fridaveAPI.getRounds(season).catch(() => []),
        ]);
        if (cancelled) return;
        setStandings(Array.isArray(sd) ? sd : []);
        const rounds = Array.isArray(rd) ? rd : [];
        setCompletedRounds(rounds.filter(r => r.status === 'completed').length);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [season]);

  if (loading) return <div className="flex justify-center py-12"><LoadingSpinner /></div>;

  return (
    <div>
      <div className="bg-white rounded-2xl p-4 mb-4 text-center border border-gray-200 shadow-sm">
        <h2 className="text-lg font-bold text-gray-900">Series Standings</h2>
        <p className="text-gray-500 text-sm mt-0.5">
          {completedRounds === 0 ? 'No rounds completed yet' : `After ${completedRounds} round${completedRounds !== 1 ? 's' : ''}`}
        </p>
        <p className="text-gray-400 text-xs mt-1">Best 3 rounds count · {season} Season</p>
      </div>

      {standings.length > 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 flex items-center text-[10px] font-bold text-gray-400 uppercase tracking-wide">
            <div className="w-8 shrink-0 text-center">Pos</div>
            <div className="flex-1 min-w-0">Player</div>
            <div className="w-10 shrink-0 text-center">Rnds</div>
            <div className="w-10 shrink-0 text-center">Pts</div>
          </div>
          {(() => {
            // Assign shared positions for tied series points
            const withPos = [];
            standings.forEach((player, index) => {
              const pos = index === 0 ? 1 : (player.series_points === standings[index - 1].series_points ? withPos[index - 1].pos : index + 1);
              const isTied = standings.some((other, i) => i !== index && other.series_points === player.series_points);
              withPos.push({ ...player, pos, isTied });
            });
            return withPos;
          })().map((player) => {
            const { pos, isTied } = player;
            const medal = pos === 1 ? '🥇' : pos === 2 ? '🥈' : pos === 3 ? '🥉' : null;
            return (
              <button
                key={player.player_id}
                onClick={() => navigate(`/fridave/players/${player.player_id}`)}
                className={`w-full px-4 py-3 flex items-center border-b border-gray-100 last:border-0 text-left hover:bg-gray-50 ${pos === 1 ? 'bg-green-50' : 'bg-white'}`}
              >
                <div className="w-8 shrink-0 text-center">
                  {medal ? (
                    <span className="text-lg">{medal}</span>
                  ) : (
                    <span className="text-sm font-bold text-gray-500">{isTied ? 'T' : ''}{pos}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0 font-semibold text-sm text-gray-900 truncate">{player.player_name}</div>
                <div className="w-10 shrink-0 text-center text-sm text-gray-500">{player.rounds_played}</div>
                <div className="w-10 shrink-0 text-center">
                  <span className={`text-base font-bold text-gray-900`}>
                    {player.series_points}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-2xl p-8 text-center border border-gray-200">
          <div className="text-4xl mb-3">🏆</div>
          <p className="text-gray-500 text-sm">No standings yet — complete a round to see results</p>
        </div>
      )}
    </div>
  );
}

// ── Eclectic ──────────────────────────────────────────────────────────────────

function EclecticPanel({ season, onPlayerSelect }) {
  const [eclectic, setEclectic] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fridaveAPI.getEclectic(season);
        if (!cancelled) setEclectic(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || err.message || 'Failed to load eclectic data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [season]);

  if (loading) return <div className="flex justify-center py-12"><LoadingSpinner /></div>;

  if (error) return (
    <div className="bg-white rounded-2xl p-8 text-center border border-gray-200">
      <p className="text-red-500 text-sm font-medium">{error}</p>
    </div>
  );

  // Only show players with scores on all 18 holes
  const complete = eclectic.filter(p => parseInt(p.holes_played) === 18);
  const eclecticWithPos = [];
  complete.forEach((player, index) => {
    if (index === 0) {
      eclecticWithPos.push({ ...player, pos: 1, isTied: false });
    } else {
      const isTied = player.eclectic_total === complete[index - 1].eclectic_total;
      const pos = isTied ? eclecticWithPos[index - 1].pos : index + 1;
      if (isTied && !eclecticWithPos[index - 1].isTied) {
        eclecticWithPos[index - 1] = { ...eclecticWithPos[index - 1], isTied: true };
      }
      eclecticWithPos.push({ ...player, pos, isTied });
    }
  });

  return (
    <div>
      <div className="bg-white rounded-2xl p-4 mb-4 text-center border border-gray-200 shadow-sm">
        <h2 className="text-lg font-bold text-gray-900">Eclectic Leaderboard</h2>
        <p className="text-gray-400 text-xs mt-1">Best gross score across all rounds</p>
        <p className="text-gray-400 text-xs mt-1">A player must register a gross score on all holes before appearing on leaderboard</p>
      </div>

      {eclecticWithPos.length > 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 flex items-center text-[10px] font-bold text-gray-400 uppercase tracking-wide">
            <div className="w-8 shrink-0 text-center">Pos</div>
            <div className="flex-1 min-w-0">Player</div>
            <div className="w-12 shrink-0 text-center">Total</div>
          </div>
          {eclecticWithPos.map((player) => (
            <button
              key={player.player_id}
              onClick={() => onPlayerSelect(player)}
              className="w-full px-4 py-3 flex items-center border-b border-gray-100 last:border-0 bg-white hover:bg-gray-50 text-left"
            >
              <div className="w-8 shrink-0 text-center text-sm font-semibold text-gray-600">
                {player.isTied ? 'T' : ''}{player.pos}
              </div>
              <div className="flex-1 min-w-0 font-semibold text-gray-900 text-sm truncate">{player.player_name}</div>
              <div className="w-12 shrink-0 text-center text-base font-bold text-gray-900">
                {player.eclectic_total}
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl p-8 text-center border border-gray-200">
          <div className="text-4xl mb-3">⛳</div>
          <p className="text-gray-500 text-sm">No eclectic data yet — complete a round first</p>
        </div>
      )}
    </div>
  );
}

// ── Events ────────────────────────────────────────────────────────────────────

function EventsPanel({ season, onEventSelect }) {
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fridaveAPI.getRounds(season).catch(() => []);
        if (!cancelled) {
          const completed = (Array.isArray(data) ? data : []).filter(r => r.status === 'completed');
          setRounds(completed);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [season]);

  if (loading) return <div className="flex justify-center py-12"><LoadingSpinner /></div>;

  return (
    <div className="space-y-3">
      {rounds.length > 0 ? rounds.map(round => (
        <button
          key={round.id}
          onClick={() => onEventSelect(round)}
          className="w-full bg-white rounded-2xl p-4 border border-gray-200 shadow-sm text-left hover:border-gray-300 transition-colors"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="font-bold text-gray-900 text-sm">{round.event_name}</p>
              <p className="text-xs text-gray-400 mt-0.5">
                {new Date(round.start_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
              {round.leading_player_name && (
                <p className="text-xs text-gray-500 font-semibold mt-1.5">🏆 {round.leading_player_name}</p>
              )}
            </div>
            <svg className="w-4 h-4 text-gray-300 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </button>
      )) : (
        <div className="bg-white rounded-2xl p-8 text-center border border-gray-200">
          <div className="text-4xl mb-3">📅</div>
          <p className="text-gray-500 text-sm">No completed events yet</p>
        </div>
      )}
    </div>
  );
}

// ── Event Detail ──────────────────────────────────────────────────────────────

function EventDetail({ round, onBack }) {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scoreTab, setScoreTab] = useState('stableford');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const lb = await roundsAPI.getLeaderboard(round.id).catch(() => []);
        if (!cancelled) setLeaderboard(Array.isArray(lb) ? lb : []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [round.id]);

  // Stableford: countback resolves all ties — positions always sequential
  const stablefordWithPos = leaderboard.map((player, index) => ({ ...player, pos: index + 1 }));

  const showCountback = stablefordWithPos.length >= 2 &&
    stablefordWithPos[0].total_points === stablefordWithPos[1].total_points;

  // Gross: sort by total_strokes ASC; NR players go to bottom
  const grossSorted = [...leaderboard]
    .filter(p => !p.has_nr)
    .sort((a, b) => (a.total_strokes || 999) - (b.total_strokes || 999));
  const grossWithPos = [];
  grossSorted.forEach((player, index) => {
    let pos;
    if (index === 0) {
      pos = 1;
    } else {
      const prev = grossWithPos[index - 1];
      pos = player.total_strokes === prev.total_strokes ? prev.pos : index + 1;
    }
    grossWithPos.push({ ...player, pos });
  });
  const nrPlayers = leaderboard.filter(p => p.has_nr).map(p => ({ ...p, pos: 'NR' }));
  const grossDisplay = [...grossWithPos, ...nrPlayers];

  const displayList = scoreTab === 'stableford' ? stablefordWithPos : grossDisplay;

  return (
    <div className="px-4 pb-4">
      <button onClick={onBack} className="text-fd-primary font-semibold flex items-center gap-1 mb-4">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Stats
      </button>

      <div className="bg-white rounded-2xl p-4 mb-4 border border-gray-200 shadow-sm">
        <p className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-0.5">Completed Event</p>
        <h2 className="text-lg font-bold text-gray-900">{round.event_name}</h2>
        <p className="text-xs text-gray-400 mt-0.5">
          {new Date(round.start_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
        </p>
      </div>

      <div className="flex gap-1 bg-white rounded-2xl p-1 mb-4 border border-gray-200">
        {['stableford', 'gross'].map(tab => (
          <button
            key={tab}
            onClick={() => setScoreTab(tab)}
            className={`flex-1 py-2 rounded-xl text-xs font-bold transition-colors ${
              scoreTab === tab ? 'bg-fd-primary text-white' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab === 'stableford' ? 'Stableford' : 'Gross'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><LoadingSpinner /></div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 flex items-center text-[10px] font-bold text-gray-400 uppercase tracking-wide">
            <div className="w-8 shrink-0 text-center">Pos</div>
            <div className="flex-1 min-w-0">Player</div>
            <div className="w-10 shrink-0 text-center">{scoreTab === 'stableford' ? 'Pts' : 'Gross'}</div>
          </div>
          {displayList.map((player, index) => {
            const isNr = player.has_nr && scoreTab === 'gross';
            // Stableford: countback resolves ties, no T marker. Gross: show T for equal strokes.
            const isTied = scoreTab === 'gross' && typeof player.pos === 'number' && displayList.some(
              other => other !== player && other.pos === player.pos
            );
            return (
              <React.Fragment key={player.round_player_id || player.player_id}>
                <button
                  onClick={() => navigate(`/fridave/rounds/${round.id}/players/${player.player_id}`)}
                  className="w-full px-4 py-3 flex items-center border-b border-gray-100 last:border-0 bg-white hover:bg-gray-50 text-left"
                >
                  <div className="w-8 shrink-0 text-center text-sm font-semibold text-gray-600">
                    {isTied ? 'T' : ''}{player.pos}
                  </div>
                  <div className="flex-1 min-w-0 font-semibold text-gray-900 text-sm truncate">{player.player_name}</div>
                  <div className={`w-10 shrink-0 text-center text-base font-bold text-gray-900`}>
                    {scoreTab === 'stableford' ? player.total_points : (isNr ? '' : player.total_strokes)}
                  </div>
                </button>
                {scoreTab === 'stableford' && showCountback && index === 0 && (
                  <div className="px-4 py-1.5 text-center text-xs text-gray-400 italic bg-gray-50 border-b border-gray-100">
                    Winner on countback
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Eclectic Scorecard Detail ─────────────────────────────────────────────────

function EclecticScorecardDetail({ player, season, onBack }) {
  const [scorecard, setScorecard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fridaveAPI.getEclecticScorecard(player.player_id, season);
        if (!cancelled) setScorecard(data);
      } catch {
        if (!cancelled) setError('Failed to load eclectic scorecard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [player.player_id, season]);

  return (
    <div className="px-4 pb-4">
      <button onClick={onBack} className="text-fd-primary font-semibold flex items-center gap-1 mb-4">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Eclectic
      </button>

      <div className="bg-white rounded-2xl p-5 mb-4 border border-gray-200 shadow-sm">
        <p className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-1">Eclectic Scorecard</p>
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">{player.player_name}</h2>
          <div className="text-right">
            <p className="text-xs text-gray-400 font-medium">
              {parseInt(player.holes_played) === 18 ? 'Total' : `${player.holes_played} holes`}
            </p>
            <p className="text-2xl font-bold text-gray-900">
              {parseInt(player.holes_played) === 18 ? player.eclectic_total : `${player.eclectic_total}`}
            </p>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-1">Best gross per hole · {season} Season</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><LoadingSpinner /></div>
      ) : error ? (
        <div className="bg-white rounded-2xl p-6 text-center border border-gray-200">
          <p className="text-gray-400 text-sm">{error}</p>
        </div>
      ) : scorecard ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <ScorecardView scorecard={scorecard} hideHeader hidePoints />
        </div>
      ) : null}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function FriDaveStats() {
  const [activeTab, setActiveTab] = useState('standings');
  const [view, setView] = useState('tabs');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const currentYear = String(new Date().getFullYear());

  if (view === 'event-detail' && selectedEvent) {
    return <EventDetail round={selectedEvent} onBack={() => setView('tabs')} />;
  }

  if (view === 'eclectic-detail' && selectedPlayer) {
    return (
      <EclecticScorecardDetail
        player={selectedPlayer}
        season={currentYear}
        onBack={() => setView('tabs')}
      />
    );
  }

  return (
    <div className="px-4 pb-4">
      {/* Sub-tabs */}
      <div className="flex gap-1 bg-white rounded-2xl p-1 mb-4 border border-gray-200 shadow-sm">
        {[
          { id: 'standings', label: 'Standings' },
          { id: 'eclectic', label: 'Eclectic' },
          { id: 'events', label: 'Events' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-2 rounded-xl text-xs font-bold transition-colors ${
              activeTab === tab.id ? 'bg-fd-primary text-white' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'standings' && <StandingsPanel season={currentYear} />}
      {activeTab === 'eclectic' && (
        <EclecticPanel
          season={currentYear}
          onPlayerSelect={(p) => { setSelectedPlayer(p); setView('eclectic-detail'); }}
        />
      )}
      {activeTab === 'events' && (
        <EventsPanel
          season={currentYear}
          onEventSelect={(e) => { setSelectedEvent(e); setView('event-detail'); }}
        />
      )}
    </div>
  );
}
